import { Injectable } from '@angular/core';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import {
  AtividadeEconomicaEmpresaConfigFormDTO,
  ContasDeRecebimentoDTO,
  DadosDeRecebimentoDTO,
  DadosDeRecebimentoStatusDTO,
  ExigenciasDoPedidoMinishopDTO,
  FormasDePagamentoMinishopDTO,
  EmpresaConfigDTO,
  EmpresaConfigPrecoCoberturaDeEnvioDTO,
  EmpresaConfigResumidoDTO,
  MiniShoppingStatusDTO,
  certificadoDigitalEmpresaConfigDTO,
} from '../../models/configuracoes/empresa.model';
import {
  ConsultaEmpresaUsuarioDTO,
  EmpresaListagemResponseDTO,
} from '../../models/configuracoes/empresa-listagem.model';

/** Endpoint da sessao da empresa - cacheado via shareReplay */
const URL_SESSAO_EMPRESA = '/configuracaoEmpresa/sessao';

@Injectable({
  providedIn: 'root',
})
export class EmpresaConfigService implements ApiInterface {
  /** Cache do GET /configuracaoEmpresa/sessao - compartilhado entre getSessao() e getById(URL_SESSAO). Invalidado em logout via clearSessaoCache(). */
  private sessao$?: Observable<EmpresaConfigDTO>;

  constructor(private apiService: ApiService) { }

  get<T>(url: string, data?: any): Observable<any> {
    const queryParams = this.apiService.buildQueryParams(data);
    const patch = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<any>(patch);
  }

  getById<T>(url: string): Observable<EmpresaConfigDTO> {
    // Reaproveita o cache da sessao para evitar chamadas duplicadas quando
    // a pagina e o menu-lateral consultam o mesmo endpoint na mesma carga.
    if (url === URL_SESSAO_EMPRESA) {
      return this.getSessaoCached();
    }
    return this.apiService.read<EmpresaConfigDTO>(url);
  }

  post<T>(url: string, data: EmpresaConfigDTO): Observable<EmpresaConfigDTO> {
    return this.apiService.create<EmpresaConfigDTO>(url, data);
  }

  postEmpresaAtividade<T>(
    url: string,
    data: AtividadeEconomicaEmpresaConfigFormDTO[]
  ): Observable<AtividadeEconomicaEmpresaConfigFormDTO> {
    return this.apiService.create<AtividadeEconomicaEmpresaConfigFormDTO>(
      url,
      data
    );
  }

  postCertificadoDigital<T>(
    url: string,
    data: certificadoDigitalEmpresaConfigDTO
  ): Observable<certificadoDigitalEmpresaConfigDTO> {
    return this.apiService.create<certificadoDigitalEmpresaConfigDTO>(
      url,
      data
    );
  }
  patchCertificadoDigital<T>(
    url: string,
    data: certificadoDigitalEmpresaConfigDTO
  ): Observable<certificadoDigitalEmpresaConfigDTO> {
    return this.apiService.patch<certificadoDigitalEmpresaConfigDTO>(
      url,
      data
    );
  }

  // No EmpresaConfigService, adicione este método:
  uploadCertificadoDigitalMultipart(file: File, senha: string): Observable<any> {
    const formData = new FormData();
    formData.append('certificadoDigital', file); // Nome igual ao @RequestParam
    formData.append('senha', senha); // Nome igual ao @RequestParam

    console.log('📤 Enviando FormData:', {
      fileName: file.name,
      fileSize: file.size,
      hasPassword: !!senha
    });

    return this.apiService.patchFormData('/configuracaoEmpresa/adicionar-certificado-digital', formData);
  }

  put<T>(url: string, data: EmpresaConfigDTO): Observable<EmpresaConfigDTO> {
    return this.apiService.update<EmpresaConfigDTO>(url, data);
  }

  /** PUT que não espera resposta completa do backend (resposta pode ser muito grande) */
  putFireAndForget(url: string, data: any): Observable<any> {
    return this.apiService.updateFireAndForget(url, data);
  }

  patchAparencia(
    empresaId: number,
    payload: EmpresaConfigResumidoDTO
  ): Observable<any> {
    return this.apiService.patch(
      `/configuracaoEmpresa/${empresaId}/aparencia`,
      payload
    );
  }

  patchPrecoCoberturaDeEnvio(payload: EmpresaConfigPrecoCoberturaDeEnvioDTO): Observable<any> {
    return this.apiService.patch(
      `/configuracaoEmpresa/preco-cobertura-de-envio`,
      payload
    );
  }

  /**
   * Habilita ou desabilita o Mini Shopping da empresa (query param `habilitar`).
   * O backend retorna a configuração completa atualizada.
   * Invalida o cache da sessao para que outras telas (produtos) releiam o valor novo.
   */
  patchHabilitarMiniShopping(habilitar: boolean): Observable<EmpresaConfigDTO> {
    this.clearSessaoCache();
    return this.apiService.patch<EmpresaConfigDTO>(
      `/configuracaoEmpresa/habilitar-mini-shopping?habilitar=${habilitar}`,
      null
    );
  }

  /**
   * Atualiza as formas de pagamento aceitas no minishop.
   *
   * PATCH dedicado — não usar o PUT genérico de configuração: ele espera os enums
   * no formato de ENTRADA (`moeda: 'GUARANI'`), enquanto o que a tela tem em mãos
   * é o DTO de consulta (`moeda: { enum: 'GUARANI' }`), o que devolve 400.
   *
   * Invalida o cache da sessão porque a tela de criar pedido lê estas flags de lá.
   */
  patchFormasDePagamentoMinishop(payload: FormasDePagamentoMinishopDTO): Observable<EmpresaConfigDTO> {
    this.clearSessaoCache();
    return this.apiService.patch<EmpresaConfigDTO>(
      '/configuracaoEmpresa/formas-pagamento-minishop',
      payload
    );
  }

  /**
   * Grava o que o pedido de consumo no local exige (mesa e comanda).
   *
   * Invalida o cache da sessão porque as telas de pedido leem esses flags de lá para decidir se
   * o campo é obrigatório.
   */
  patchExigenciasDoPedidoMinishop(payload: ExigenciasDoPedidoMinishopDTO): Observable<EmpresaConfigDTO> {
    this.clearSessaoCache();
    return this.apiService.patch<EmpresaConfigDTO>(
      '/configuracaoEmpresa/exigencias-pedido-minishop',
      payload
    );
  }

  /**
   * Consulta se os dados de recebimento (Bancard) já foram configurados.
   *
   * Não existe endpoint que devolva os valores — nem o código do comércio, nem as chaves.
   * A tela mostra apenas "Configurado" / "Não configurado".
   */
  getDadosDeRecebimento(): Observable<DadosDeRecebimentoStatusDTO> {
    return this.apiService.read<DadosDeRecebimentoStatusDTO>('/configuracaoEmpresa/dados-recebimento');
  }

  /**
   * Grava os dados de recebimento (Bancard). Somente o administrador da empresa;
   * o backend responde 403 para os demais.
   *
   * Invalida o cache da sessão porque `bancardConfigurado` muda e a tela de vendas
   * depende dele para liberar a forma QR Code.
   */
  salvarDadosDeRecebimento(payload: DadosDeRecebimentoDTO): Observable<DadosDeRecebimentoStatusDTO> {
    this.clearSessaoCache();
    return this.apiService.update<DadosDeRecebimentoStatusDTO>(
      '/configuracaoEmpresa/dados-recebimento',
      payload
    );
  }

  /**
   * Grava as contas onde o dinheiro cobrado pela Bancard entra (uma por forma).
   *
   * PATCH separado do `salvarDadosDeRecebimento`: aquele exige as chaves, que a tela nunca
   * consegue reler — trocar só a conta não pode obrigar a digitar tudo de novo.
   *
   * Invalida o cache da sessão porque `minishopContaQrCodeId`/`minishopContaTarjetaId` vêm de lá.
   */
  patchContasDeRecebimento(payload: ContasDeRecebimentoDTO): Observable<DadosDeRecebimentoStatusDTO> {
    this.clearSessaoCache();
    return this.apiService.patch<DadosDeRecebimentoStatusDTO>(
      '/configuracaoEmpresa/contas-recebimento',
      payload
    );
  }

  /**
   * Consulta o status atualizado do Mini Shopping (habilitado + URL).
   * Usar a cada ação que dependa do estado, para garantir dados frescos.
   */
  getStatusMiniShopping(): Observable<MiniShoppingStatusDTO> {
    return this.apiService.read<MiniShoppingStatusDTO>('/configuracaoEmpresa/mini-shopping/status');
  }

  getSessao(): Observable<any> {
    return this.getSessaoCached();
  }

  /** Retorna o observable cacheado da sessao (1 unica chamada HTTP por sessao de usuario). */
  private getSessaoCached(): Observable<EmpresaConfigDTO> {
    if (!this.sessao$) {
      this.sessao$ = this.apiService.read<EmpresaConfigDTO>(URL_SESSAO_EMPRESA).pipe(
        shareReplay(1),
      );
    }
    return this.sessao$;
  }

  /** Invalida o cache da sessao. Chamado no logout para evitar cache entre usuarios. */
  clearSessaoCache(): void {
    this.sessao$ = undefined;
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  /**
   * Lista as empresas para troca de empresa ativa (feature MASTER).
   * GET /configuracaoEmpresa?busca={termo}&page={n}&size={size}. So usuario
   * master recebe a lista completa. O `busca` filtra por nome/fantasia/email/ruc
   * e telefone (backend). Retorna Spring Page.
   */
  listarEmpresas(
    busca: string,
    page: number,
    size: number,
    sort?: string,
  ): Observable<EmpresaListagemResponseDTO> {
    const params: Record<string, unknown> = { page, size };
    if (busca && busca.trim().length > 0) {
      params['busca'] = busca.trim();
    }
    // Ordenacao server-side no formato do Spring ("campo,asc").
    if (sort) {
      params['sort'] = sort;
    }
    const queryParams = this.apiService.buildQueryParams(params);
    return this.apiService.read<EmpresaListagemResponseDTO>(
      `/configuracaoEmpresa?${queryParams}`,
    );
  }

  /**
   * Troca a empresa ativa do usuario autenticado.
   * PATCH /usuario/alterar-empresa/{empresaId}. Invalida o cache da sessao para
   * que o proximo carregamento reflita a nova empresa.
   */
  alterarEmpresa(empresaId: number): Observable<ConsultaEmpresaUsuarioDTO> {
    return this.apiService.patch<ConsultaEmpresaUsuarioDTO>(
      `/usuario/alterar-empresa/${empresaId}`,
      null,
    );
  }
}
